import React, { useState } from "react";
import { Button, Dropdown, Modal } from "antd";
import {
  MoreOutlined,
  FilePdfOutlined,
  ToolOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import styles from "./style.module.scss";

export function reportNeedsWidgetRepair(report) {
  if (!report) return false;
  const missing = Number(report.widgets_missing) || 0;
  const failed = Number(report.widgets_failed) || 0;
  const notEnough = Number(report.widgets_not_enough_data) || 0;
  const emptyGenai = Number(report.widgets_empty_genai_signal) || 0;
  return missing + failed + notEnough + emptyGenai > 0;
}

export function canRegenerateReportPdf(report, displayStatus) {
  const status = displayStatus || report?.display_status || report?.status;
  if (status === "ready" || status === "finalizing") return true;
  // Partial: allow PDF regen even before first pdf_url (ready widgets only on API).
  if (status === "partial" && (Number(report?.widgets_ready) || 0) > 0) return true;
  return false;
}

/**
 * Card header overflow (⋯): PDF update, optional repair, or full GenAI regen.
 */
export function ReportCardOverflowMenu({
  report,
  displayStatus,
  disabled = false,
  onRegeneratePdf,
  onRepairWidgets,
  onRegenerateAll,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const showPdf = canRegenerateReportPdf(report, displayStatus);
  const showRepair = reportNeedsWidgetRepair(report);

  const confirmRepair = () => {
    setOpen(false);
    Modal.confirm({
      title: t("ai_services.regenerate_repair_confirm_title"),
      content: t("ai_services.regenerate_repair_confirm_desc"),
      okText: t("yes"),
      cancelText: t("no"),
      onOk: () => {
        if (onRepairWidgets) onRepairWidgets(report);
      },
    });
  };

  const confirmAll = () => {
    setOpen(false);
    if (onRegenerateAll) onRegenerateAll(report);
  };

  const menuItems = [];

  if (showPdf) {
    menuItems.push({
      key: "regenerate-pdf",
      label: t("ai_services.regenerate_pdf_only"),
      icon: <FilePdfOutlined />,
      onClick: () => {
        setOpen(false);
        if (onRegeneratePdf) onRegeneratePdf(report);
      },
    });
  }

  if (showRepair) {
    menuItems.push({
      key: "repair-widgets",
      label: t("ai_services.regenerate_repair_widgets"),
      icon: <ToolOutlined />,
      onClick: confirmRepair,
    });
  }

  if (menuItems.length > 0) {
    menuItems.push({ type: "divider" });
  }

  menuItems.push({
    key: "regenerate-all",
    label: (
      <span className={styles.reportRegenAllLabel}>
        {t("ai_services.regenerate_all_widgets")}
      </span>
    ),
    icon: <ReloadOutlined />,
    danger: true,
    onClick: confirmAll,
  });

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={["click"]}
      placement="bottomRight"
      open={open}
      onOpenChange={setOpen}
      overlayClassName={styles.reportCardDropdown}
      getPopupContainer={() => document.body}
    >
      <Button
        size="small"
        icon={<MoreOutlined />}
        disabled={disabled}
        className={`${styles.moreMenuBtn} ${open ? styles.moreMenuBtnActive : ""}`}
        onClick={(e) => e.stopPropagation()}
        aria-label={t("ai_services.regenerate_menu_aria")}
      />
    </Dropdown>
  );
}

export function RegenerateAllConfirmContent({ preview, loading, t }) {
  if (loading) {
    return <span>{t("ai_services.regenerate_all_preview_loading")}</span>;
  }
  if (!preview) {
    return <span>{t("ai_services.regenerate_all_confirm_desc")}</span>;
  }
  const credits = Number(preview.credits_required) || 0;
  const count = Number(preview.genai_widget_count) || 0;
  return (
    <div className={styles.regenerateAllConfirmBody}>
      <p>{t("ai_services.regenerate_all_confirm_desc")}</p>
      <p>
        <ExclamationCircleOutlined style={{ color: "#faad14", marginRight: 6 }} />
        {t("ai_services.regenerate_all_credit_hint", {
          count,
          credits,
        })}
      </p>
      {preview.sufficient === false && !preview.unlimited ? (
        <p className={styles.regenerateAllInsufficient}>
          {t("ai_services.regenerate_all_insufficient_credits", {
            remaining: preview.credits_remaining ?? 0,
            required: credits,
          })}
        </p>
      ) : null}
    </div>
  );
}
